import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Check, Save, Lock, MapPin, Briefcase, Trash2,
  AlertTriangle, SlidersHorizontal, Search, HelpCircle,
  Utensils, ShoppingBag, Heart, Sparkles, BookOpen,
  Building2 as Building, Gamepad2, Dumbbell, Car, Hotel,
  Palette, Cpu, Factory, Users, Leaf, Plus, X,
} from "lucide-react";
import { getActiveProfile, updateProfile, createProfile, getProfileLimits, getProfileLimitDefaults, getProfileSlots } from "../lib/api";
import { useOwnedGrids } from "../context/OwnedGridsContext";
import { idLabel } from "../lib/idLabels";

const ACCENT = "#14543e";
const API_BASE = import.meta.env.VITE_API_URL || "";

const ICON_MAP = {
  Utensils, ShoppingBag, Heart, Sparkles, Briefcase, BookOpen,
  Building, Gamepad2, Dumbbell, Car, Hotel, Palette, Cpu, Factory, Users, Leaf,
};

// ─── Shared primitives ────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 mb-6">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
        style={{ backgroundColor: `${ACCENT}15` }}>
        <Icon className="w-4.5 h-4.5" style={{ color: ACCENT }} />
      </div>
      <div>
        <h2 className="text-base font-bold text-[#202124]">{title}</h2>
        {subtitle && <p className="text-xs text-[#5f6368] mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

function FieldLabel({ children, hint }) {
  return (
    <div className="mb-2">
      <p className="text-[13px] font-medium text-[#202124]">{children}</p>
      {hint && <p className="text-[11px] text-[#5f6368] mt-0.5">{hint}</p>}
    </div>
  );
}

function ChipGroup({ options, value, onChange, multi = false, danger = false }) {
  function toggle(v) {
    if (multi) onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
    else onChange(value === v ? "" : v);
  }
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const sel = multi ? value.includes(o) : value === o;
        let style = {};
        let cls = "px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all";

        if (sel && danger) {
          style = { background: "#ba1a1a", borderColor: "#ba1a1a", color: "#fff" };
        } else if (sel) {
          style = { background: ACCENT, borderColor: ACCENT, color: "#fff" };
        } else if (danger) {
          cls += " bg-white text-[#40484a] border-[#dadce0] hover:border-red-300 hover:text-red-600";
        } else {
          cls += " bg-white text-[#3c4043] border-[#dadce0] hover:bg-[#f8f9fa]";
        }

        return (
          <button key={o} onClick={() => toggle(o)} style={style} className={cls}>
            {danger && sel && <span className="mr-1">✕</span>}
            {!danger && sel && multi && <span className="mr-1 opacity-70">✓</span>}
            {idLabel(o)}
          </button>
        );
      })}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ outline: "none" }}
      onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
      onBlurCapture={(e) => (e.target.style.borderColor = "#dadce0")}
      className="w-full px-4 py-2.5 border border-[#dadce0] rounded-xl text-sm text-[#202124] placeholder-[#9aa0a6] bg-white transition-colors"
    />
  );
}

// Rupiah amount with thousand separators on display, plain Number in state.
function RupiahField({ value, onChange, placeholder }) {
  const shown = value === "" || value == null ? "" : Number(value).toLocaleString("id-ID");
  return (
    <div className="flex items-center gap-2">
      <span className="shrink-0 text-sm font-medium text-[#5f6368]">Rp</span>
      <input
        inputMode="numeric"
        value={shown}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "");
          onChange(digits === "" ? "" : Number(digits));
        }}
        placeholder={placeholder}
        style={{ outline: "none" }}
        onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
        onBlurCapture={(e) => (e.target.style.borderColor = "#dadce0")}
        className="w-full px-4 py-2.5 border border-[#dadce0] rounded-xl text-sm text-[#202124] placeholder-[#9aa0a6] bg-white transition-colors"
      />
    </div>
  );
}

function PasswordInput({ label, placeholder }) {
  const [val, setVal] = useState("");
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <TextInput type="password" value={val} onChange={setVal} placeholder={placeholder} />
    </div>
  );
}

// ─── City Autocomplete ────────────────────────────────────────────────────────

function CityAutocomplete({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/kecamatan/cities`)
      .then((r) => r.json())
      .then((d) => setCities(d))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (value?.nama_kabkot) {
      setQuery(`${value.nama_kabkot}, ${value.nama_provinsi}`);
    }
  }, [value?.nama_kabkot]);

  useEffect(() => {
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const filtered = query.length >= 2
    ? cities.filter((c) =>
        `${c.nama_provinsi} ${c.nama_kabkot}`.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 8)
    : [];

  function select(city) {
    setQuery(`${city.nama_kabkot}, ${city.nama_provinsi}`);
    onChange({ kode_kabkot: city.kode_kabkot, nama_kabkot: city.nama_kabkot, nama_provinsi: city.nama_provinsi });
    setOpen(false);
  }

  function handleInput(e) {
    setQuery(e.target.value);
    onChange(null);
    setOpen(true);
  }

  return (
    <div ref={ref} className="relative">
      <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9aa0a6] pointer-events-none" />
      <input
        value={query}
        onChange={handleInput}
        onFocus={() => query && setOpen(true)}
        placeholder="Cari kota / kabupaten..."
        style={{ outline: "none" }}
        onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
        onBlurCapture={(e) => (e.target.style.borderColor = "#dadce0")}
        className="w-full pl-10 pr-4 py-2.5 border border-[#dadce0] rounded-xl text-sm text-[#202124] placeholder-[#9aa0a6] bg-white transition-colors"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white rounded-2xl shadow-[0_12px_32px_rgba(25,28,29,0.06)] border border-[#dadce0] overflow-y-auto max-h-52">
          {filtered.map((c) => (
            <button key={c.kode_kabkot} onMouseDown={() => select(c)}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-[#f8f9fa] text-left transition-colors border-b border-[#f1f3f4] last:border-0">
              <MapPin className="w-3.5 h-3.5 text-[#9aa0a6] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#202124]">{c.nama_kabkot}</p>
                <p className="text-xs text-[#5f6368] mt-0.5">{c.nama_provinsi}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Business Type Selector ───────────────────────────────────────────────────

function sortAlpha(arr, keyFn = (x) => x) {
  return [...arr].sort((a, b) => {
    const ka = keyFn(a).toLowerCase();
    const kb = keyFn(b).toLowerCase();
    if (ka.startsWith("other") && !kb.startsWith("other")) return 1;
    if (!ka.startsWith("other") && kb.startsWith("other")) return -1;
    return ka.localeCompare(kb);
  });
}

function BizTypeSelector({ category, subtype, onCategory, onSubtype }) {
  const [categories, setCategories] = useState([]);
  const [subSearch, setSubSearch] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/api/categories`)
      .then((r) => r.json())
      .then((d) => {
        const cats = sortAlpha(d.categories, (c) => c.name || c.id || "").map((c) => ({
          ...c,
          subcategories: c.subcategories ? sortAlpha(c.subcategories) : [],
        }));
        setCategories(cats);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { setSubSearch(""); }, [category]);

  const active   = categories.find((c) => c.id === category);
  const filtered = active
    ? active.subcategories.filter((s) => s.toLowerCase().includes(subSearch.toLowerCase()))
    : [];
  const isCustom = active && subtype && !active.subcategories.includes(subtype);

  if (categories.length === 0) {
    return <div className="h-9 flex items-center"><span className="text-xs text-[#9aa0a6]">Memuat kategori...</span></div>;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => {
          const Icon = ICON_MAP[cat.icon] ?? HelpCircle;
          const sel  = category === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => { onCategory(cat.id); onSubtype(""); }}
              style={sel ? { background: ACCENT, borderColor: ACCENT } : {}}
              className={`flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full border shrink-0 text-xs font-medium transition-all ${
                sel ? "text-white border-transparent" : "border-[#dadce0] bg-white text-[#3c4043] hover:bg-[#f8f9fa]"
              }`}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              <span className="whitespace-nowrap">{cat.name}</span>
            </button>
          );
        })}
      </div>

      {active && (
        <div className="mt-3 rounded-2xl border border-[#dadce0] overflow-hidden bg-white">
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#f8f9fa] border-b border-[#f1f3f4]">
            <div className="flex items-center gap-2 min-w-0">
              {(() => { const Icon = ICON_MAP[active.icon] ?? HelpCircle; return <Icon className="w-3.5 h-3.5 text-[#9aa0a6] shrink-0" />; })()}
              <span className="text-xs font-medium text-[#202124]">{active.name}</span>
              {subtype && (
                <>
                  <span className="text-[#dadce0] text-xs">·</span>
                  <span className="text-xs font-medium truncate max-w-[140px]" style={{ color: isCustom ? "#5f6368" : ACCENT }}>
                    {subtype}
                  </span>
                </>
              )}
            </div>
            {active.subcategories.length > 10 && (
              <div className="relative shrink-0">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-[#9aa0a6] pointer-events-none" />
                <input
                  value={subSearch}
                  onChange={(e) => setSubSearch(e.target.value)}
                  placeholder="Saring..."
                  style={{ outline: "none" }}
                  onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
                  onBlurCapture={(e) => (e.target.style.borderColor = "#dadce0")}
                  className="w-24 pl-7 pr-2.5 py-1 text-xs border border-[#dadce0] rounded-full bg-white transition-colors"
                />
              </div>
            )}
          </div>

          <div className="p-4 flex flex-wrap gap-2 max-h-[188px] overflow-y-auto">
            {filtered.length > 0 ? (
              filtered.map((sub) => {
                const sel = subtype === sub;
                return (
                  <button
                    key={sub}
                    onClick={() => onSubtype(sel ? "" : sub)}
                    style={sel ? { background: ACCENT, borderColor: ACCENT, color: "#fff" } : {}}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      sel ? "border-transparent" : "border-[#dadce0] bg-white text-[#3c4043] hover:bg-[#f8f9fa]"
                    }`}
                  >
                    {sub}
                  </button>
                );
              })
            ) : (
              <p className="text-xs text-[#9aa0a6] py-1">Tidak ada hasil untuk &ldquo;{subSearch}&rdquo;</p>
            )}
          </div>

          {active.allow_custom_input && (
            <div className="flex items-center gap-2.5 px-4 pb-4 pt-3 border-t border-[#f1f3f4]">
              <span className="text-[11px] font-medium text-[#5f6368] shrink-0">Tipe khusus:</span>
              <input
                value={isCustom ? subtype : ""}
                onChange={(e) => onSubtype(e.target.value)}
                placeholder={`misalnya, tipe ${active.name.toLowerCase()} unik saya`}
                style={{ outline: "none" }}
                onFocusCapture={(e) => (e.target.style.borderBottomColor = ACCENT)}
                onBlurCapture={(e) => (e.target.style.borderBottomColor = "#dadce0")}
                className="flex-1 text-xs py-1 border-b border-[#dadce0] bg-transparent text-[#202124] placeholder-[#9aa0a6] transition-colors"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Demographic Selector ─────────────────────────────────────────────────────

const DEMO_PRESETS = [
  { id: "young",         label: "Dewasa Muda", desc: "Gen Z & Milenial",      values: { "Male|<18": 2, "Male|18–24": 22, "Male|25–34": 18, "Male|35–44": 3, "Male|45+": 2, "Female|<18": 2, "Female|18–24": 22, "Female|25–34": 18, "Female|35–44": 8, "Female|45+": 3 } },
  { id: "professionals", label: "Profesional", desc: "Pekerja dewasa 25–44",  values: { "Male|<18": 0, "Male|18–24": 8, "Male|25–34": 22, "Male|35–44": 18, "Male|45+": 5, "Female|<18": 0, "Female|18–24": 8, "Female|25–34": 22, "Female|35–44": 12, "Female|45+": 5 } },
  { id: "families",      label: "Keluarga",    desc: "Orang tua dengan anak", values: { "Male|<18": 8, "Male|18–24": 3, "Male|25–34": 12, "Male|35–44": 15, "Male|45+": 8, "Female|<18": 8, "Female|18–24": 3, "Female|25–34": 15, "Female|35–44": 18, "Female|45+": 10 } },
  { id: "seniors",       label: "Lansia",      desc: "45 tahun ke atas",       values: { "Male|<18": 2, "Male|18–24": 3, "Male|25–34": 5, "Male|35–44": 10, "Male|45+": 25, "Female|<18": 2, "Female|18–24": 3, "Female|25–34": 5, "Female|35–44": 10, "Female|45+": 35 } },
  { id: "all",           label: "Semua Usia",  desc: "Audiens campuran luas", values: { "Male|<18": 5, "Male|18–24": 10, "Male|25–34": 10, "Male|35–44": 10, "Male|45+": 5, "Female|<18": 5, "Female|18–24": 10, "Female|25–34": 15, "Female|35–44": 10, "Female|45+": 5 } },
];

const GENDER_COLORS = { Male: "#104230", Female: "#1d9160" };
const AGE_GROUPS    = ["<18", "18–24", "25–34", "35–44", "45+"];

function initAgeGender() {
  const keys = ["Male", "Female"].flatMap((g) =>
    AGE_GROUPS.map((a) => `${g}|${a}`)
  );
  const result = {};
  keys.forEach((k) => { result[k] = 10; });
  return result;
}

function redistribute(segments, changedKey, newVal) {
  const clamped = Math.max(0, Math.min(100, newVal));
  const otherKeys = Object.keys(segments).filter((k) => k !== changedKey);
  const totalOthers = otherKeys.reduce((s, k) => s + segments[k], 0);
  const remaining = 100 - clamped;
  const next = { ...segments, [changedKey]: clamped };
  if (totalOthers === 0) {
    const each = remaining / otherKeys.length;
    otherKeys.forEach((k) => { next[k] = Math.floor(each); });
  } else {
    otherKeys.forEach((k) => { next[k] = Math.floor((segments[k] / totalOthers) * remaining); });
  }
  let diff = 100 - Object.values(next).reduce((s, v) => s + v, 0);
  const sorted = [...otherKeys].sort((a, b) => next[b] - next[a]);
  for (let i = 0; diff !== 0 && i < sorted.length; i++) {
    next[sorted[i]] += diff > 0 ? 1 : -1;
    diff += diff > 0 ? -1 : 1;
  }
  return next;
}

function DemographicSelector({ value, onChange }) {
  const [custom, setCustom] = useState(false);
  const [activePreset, setActivePreset] = useState(null);

  function applyPreset(p) {
    setActivePreset(p.id);
    setCustom(false);
    onChange(p.values);
  }

  const barSegs = ["Male", "Female"].flatMap((g) =>
    AGE_GROUPS.map((a) => ({ key: `${g}|${a}`, g, pct: value[`${g}|${a}`] ?? 0 }))
  ).filter((s) => s.pct > 0);

  return (
    <div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {DEMO_PRESETS.map((p) => {
          const sel = activePreset === p.id && !custom;
          return (
            <button
              key={p.id}
              onClick={() => applyPreset(p)}
              style={sel ? { background: ACCENT, borderColor: ACCENT } : {}}
              className={`flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-xl text-left transition-all border ${
                sel ? "border-transparent" : "border-[#dadce0] bg-white hover:bg-[#f8f9fa]"
              }`}
            >
              <div className="flex items-center justify-between w-full">
                <span className={`text-xs font-medium ${sel ? "text-white" : "text-[#202124]"}`}>{p.label}</span>
                {sel && <Check className="w-3 h-3 text-white/70" />}
              </div>
              <span className={`text-[10px] ${sel ? "text-white/60" : "text-[#5f6368]"}`}>{p.desc}</span>
            </button>
          );
        })}
        <button
          onClick={() => { setCustom(true); setActivePreset(null); }}
          style={custom ? { background: ACCENT, borderColor: ACCENT } : {}}
          className={`flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-xl text-left transition-all border ${
            custom ? "border-transparent" : "border-[#dadce0] bg-white hover:bg-[#f8f9fa]"
          }`}
        >
          <div className="flex items-center justify-between w-full">
            <span className={`text-xs font-medium ${custom ? "text-white" : "text-[#202124]"}`}>Kustom</span>
            <SlidersHorizontal className={`w-3 h-3 ${custom ? "text-white/70" : "text-[#5f6368]"}`} />
          </div>
          <span className={`text-[10px] ${custom ? "text-white/60" : "text-[#5f6368]"}`}>Atur manual</span>
        </button>
      </div>

      <div className="flex rounded-full overflow-hidden h-1.5 mb-2 bg-[#f1f3f4]">
        {barSegs.map(({ key, g, pct }) => (
          <div key={key}
            style={{ width: `${pct}%`, backgroundColor: GENDER_COLORS[g], opacity: 0.85 }}
            className="transition-all duration-150" />
        ))}
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          {["Male", "Female"].map((g) => (
            <div key={g} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: GENDER_COLORS[g] }} />
              <span className="text-[11px] text-[#5f6368]">
                {g} {["Male", "Female"].flatMap((gg) => AGE_GROUPS.map((a) => `${gg}|${a}`))
                  .filter((k) => k.startsWith(g))
                  .reduce((s, k) => s + (value[k] ?? 0), 0)}%
              </span>
            </div>
          ))}
        </div>
        <span className="text-[11px] font-medium" style={{ color: ACCENT }}>
          {Object.values(value).reduce((s, v) => s + v, 0)}% total
        </span>
      </div>

      {custom && (
        <div className="border-t border-[#f1f3f4] pt-4">
          {["Male", "Female"].map((gender) => (
            <div key={gender} className="mb-4">
              <p className="text-[10px] font-medium uppercase tracking-widest mb-2.5"
                style={{ color: GENDER_COLORS[gender] }}>{gender}</p>
              {AGE_GROUPS.map((age) => {
                const key = `${gender}|${age}`;
                const pct = value[key] ?? 0;
                return (
                  <div key={key} className="flex items-center gap-3 mb-2">
                    <span className="text-[11px] text-[#5f6368] w-10 shrink-0">{age}</span>
                    <input type="range" min={0} max={100} value={pct}
                      onChange={(e) => onChange(redistribute(value, key, +e.target.value))}
                      className="flex-1 cursor-pointer h-1"
                      style={{ accentColor: GENDER_COLORS[gender] }} />
                    <span className="text-[11px] font-medium text-[#3c4043] w-7 text-right tabular-nums">{pct}%</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIORITIES      = ["High foot traffic", "High street visibility", "High purchasing power", "Low competition", "Easy access & parking", "Near anchor tenants", "Delivery-friendly coverage"];
const BUDGETS         = ["Varies by location", "Under 5M", "5–15M", "15–30M", "30–100M"];
const ENVIRONMENTS    = ["Office district", "Residential area", "School / university area", "Shopping district", "Traditional market / Pasar", "Transit hub / Station area", "Tourism area", "Hospital / healthcare cluster", "Industrial area", "Highway / main road corridor"];
// Kept in sync with Onboarding.jsx — only conditions scoring_engine._avoid_triggers
// can actually detect. Six data-less options were removed 2026-07.
const AVOID_CONDITIONS = ["Low purchasing power", "Very low foot traffic", "Far from public transit", "Low parking availability", "Poor road / street access", "Hard truck/logistics access"];
const CUSTOMERS       = ["Students", "Office workers", "Young adults / Gen Z", "Families", "Stay-at-home parents", "Senior citizens", "Tourists", "Commuters / transit users", "MSMEs / business owners", "High-income professionals"];
// Kept in sync with Onboarding.jsx. Purely the target customer's income tier —
// price lives in Trade Areas. Maps to target_ses, the strongest lever in the score.
const MARKET_TIERS    = ["Low income", "Lower-middle income", "Middle income", "Upper-middle income", "High income"];
const INCOME_TO_SES   = {
  "Low income":          "low_income",
  "Lower-middle income": "low_middle",
  "Middle income":       "middle_income",
  "Upper-middle income": "upper_income",
  "High income":         "upper_income",
};
const FREQ_UNITS      = ["per week", "per month", "per year"];
const BUSY_TIMES      = ["Morning rush (6–10am)", "Lunch hours (11am–2pm)", "Afternoon (2–5pm)", "Evening / dinner (5–9pm)", "Late night (9pm+)", "Weekend-focused", "All-day / consistent"];
const REFERENCE_TYPES = [
  { id: "Direct Competitor",   desc: "Menjual produk atau layanan yang sama ke pelanggan target yang sama." },
  { id: "Indirect Competitor", desc: "Memenuhi kebutuhan yang sama tapi dengan solusi atau format berbeda." },
  { id: "Aspirational",        desc: "Brand yang ingin Anda tiru atau jadikan tolok ukur performa." },
  { id: "Strategic",           desc: "Bukan rival langsung, tapi penting bagi positioning atau strategi pasar Anda." },
  { id: "Complementary",       desc: "Melengkapi penawaran Anda — pelanggan mengunjungi keduanya, bukan salah satu." },
  { id: "Preference",          desc: "Alternatif konsumen yang pelanggannya berpotensi menjadi pelanggan Anda." },
];
// Kept in sync with Onboarding.jsx — one option per DISTINCT signal.
const POI_OPTIONS     = [
  "Bus stop / Halte", "Train / MRT / Bus terminal",
  "School / Kindergarten", "University / Campus",
  "Hospital / Clinic / Pharmacy",
  "Restaurant / Cafe / F&B", "Traditional Market",
  "Mall / Supermarket", "Minimarket / Retail shops",
  "Salon / Barbershop", "Laundry / Daily services", "Bank / ATM", "Gas Station",
  "Office / Co-working", "Apartment / Housing complex",
  "Cinema / Karaoke", "Gym / Sports center", "Hotel", "Park / Green space",
  "Place of worship", "Government / Post office",
];

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ManageProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    businessName: "",
    bizCategory: "",
    bizSubtype: "",
    priorities: [],
    city: null,
    gridResolution: "100m",
    populationCatchment: "5km",
    rentalBudget: "",
    environments: [],
    avoidConditions: [],
    primaryCustomers: [],
    ageGroups: initAgeGender(),
    marketTier: "",
    // Trade Areas
    productPriceMin: "",
    productPriceMax: "",
    basketSize: "",
    buyingFrequency: "",
    buyingFrequencyUnit: "per month",
    marketExpectation: "",
    busyTimes: [],
    benchmarks: [{ name: "", type: "Direct Competitor" }],
    poiPriorities: [],
  });
  const [saved, setSaved] = useState(false);
  const [slots, setSlots] = useState(null);
  const [limits, setLimits] = useState({ max_poi_priorities: 5, max_anchors: 5 });
  const { ownedZones, clearZones: clearOwnedZones } = useOwnedGrids();
  const zoneCount = ownedZones.length;

  useEffect(() => { getProfileSlots().then(setSlots).catch(() => setSlots({ canCreate: true })); }, []);

  useEffect(() => {
    // Source of truth is the DB active profile; fall back to the local cache.
    getActiveProfile()
      .then((p) => {
        const src = p || JSON.parse(localStorage.getItem("landhep_profile") || "{}");
        const loadedBenchmarks = (src.benchmarks || []).filter((b) => (b.name || "").trim());
        setProfile((prev) => ({
          ...prev,
          ...src,
          ageGroups: src.ageGroups || initAgeGender(),
          avoidConditions: src.avoidConditions || [],
          benchmarks: loadedBenchmarks.length > 0 ? loadedBenchmarks : [{ name: "", type: "Direct Competitor" }],
        }));
        (p?.id ? getProfileLimits(p.id) : getProfileLimitDefaults()).then(setLimits).catch(() => {});
      })
      .catch(() => {
        try {
          const stored = JSON.parse(localStorage.getItem("landhep_profile") || "{}");
          const loadedBenchmarks = (stored.benchmarks || []).filter((b) => (b.name || "").trim());
          setProfile((prev) => ({ ...prev, ...stored, ageGroups: stored.ageGroups || initAgeGender(), avoidConditions: stored.avoidConditions || [], benchmarks: loadedBenchmarks.length > 0 ? loadedBenchmarks : [{ name: "", type: "Direct Competitor" }] }));
        } catch {}
        getProfileLimitDefaults().then(setLimits).catch(() => {});
      });
  }, []);

  function set(key, val) {
    setProfile((f) => ({ ...f, [key]: val }));
  }

  async function handleSave() {
    try {
      const payload = { ...profile, target_ses: INCOME_TO_SES[profile.marketTier] || "all", benchmarks: (profile.benchmarks || []).filter((b) => (b.name || "").trim()) };
      if (profile.id) await updateProfile(profile.id, payload);
      else            await createProfile(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert(e.message || "Gagal menyimpan profil.");
    }
  }

  function handleClearZones() {
    if (zoneCount === 0) { alert("Tidak ada zona tersimpan untuk dihapus."); return; }
    const ok = window.confirm(
      `Hapus ${zoneCount} zona tersimpan?\n\nPeringatan: Jika kamu ingin melihat laporan Analisis Mendalam zona tersebut lagi, kamu harus membayar ulang.`
    );
    if (!ok) return;
    clearOwnedZones();
  }

  return (
    <div className="min-h-full bg-[#f8f9fa]">
      <div className="max-w-2xl mx-auto px-6 py-10">

        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-xs font-medium text-[#5f6368] hover:text-[#202124] transition-colors mb-8"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Kembali
        </button>

        <h1 className="text-2xl font-bold text-[#202124] mb-1">Kelola Profil</h1>
        <p className="text-sm text-[#5f6368] mb-8">Perbarui pengaturan akun dan bisnis Anda.</p>

        {/* ── Business Profiles ── */}
        <div className="bg-white rounded-2xl border border-[#e8eaed] p-6 mb-5">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-[#14543e] shrink-0" />
                <h2 className="text-base font-bold text-[#202124]">Profil Bisnis</h2>
              </div>
              <p className="text-xs text-[#5f6368] mt-1">
                Setiap bisnis adalah profil terpisah (kota, grid, dan tagihannya sendiri).
                {slots && <> · <span className="font-semibold text-[#202124]">{slots.used} dari {slots.max ?? "∞"}</span> terpakai</>}
              </p>
            </div>
            {!slots ? null : !slots.canCreate ? (
              <span className="shrink-0 text-[11px] text-[#9aa0a6] text-right max-w-[180px] leading-snug">
                Batas profil tercapai — hubungi admin Anda untuk menaikkannya.
              </span>
            ) : (
              <button
                onClick={() => navigate("/onboarding?new=1")}
                className="shrink-0 flex items-center gap-1.5 rounded-xl bg-[#14543e] px-4 py-2.5 text-xs font-bold text-white hover:bg-[#104230] transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Profil bisnis baru
              </button>
            )}
          </div>
        </div>

        {/* ── Security ── */}
        <div className="bg-white rounded-2xl border border-[#e8eaed] p-6 mb-5">
          <SectionHeader icon={Lock} title="Keamanan" subtitle="Perubahan kata sandi belum aktif." />
          <div className="flex flex-col gap-4 opacity-60 pointer-events-none select-none">
            <PasswordInput label="Kata Sandi Saat Ini" placeholder="••••••••" />
            <PasswordInput label="Kata Sandi Baru" placeholder="Min. 8 karakter" />
            <PasswordInput label="Konfirmasi Kata Sandi Baru" placeholder="Ulangi kata sandi baru" />
          </div>
          <p className="mt-4 text-[11px] text-[#9aa0a6] flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Pengelolaan kata sandi akan segera hadir.
          </p>
        </div>

        {/* ── Business Settings ── */}
        <div className="bg-white rounded-2xl border border-[#e8eaed] p-6 mb-5">
          <SectionHeader icon={Briefcase} title="Pengaturan Bisnis" subtitle="Perubahan langsung berlaku untuk profil analisis Anda." />

          <div className="flex flex-col gap-6">
            <div>
              <FieldLabel>Nama Bisnis</FieldLabel>
              <TextInput
                value={profile.businessName}
                onChange={(v) => set("businessName", v)}
                placeholder="misalnya, Warung Barokah, Kopi Kenangan..."
              />
            </div>

            <div>
              {profile.allowCategoryChange ? (
                <>
                  <FieldLabel hint="Admin Anda telah membuka kunci ini — Anda bisa mengubah jenis bisnis.">
                    Kategori &amp; Subtipe Bisnis
                  </FieldLabel>
                  <BizTypeSelector
                    category={profile.bizCategory}
                    subtype={profile.bizSubtype}
                    onCategory={(v) => set("bizCategory", v)}
                    onSubtype={(v) => set("bizSubtype", v)}
                  />
                </>
              ) : (
                <>
                  <FieldLabel>Kategori &amp; Subtipe Bisnis</FieldLabel>
                  <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex items-start gap-3">
                      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-700">
                          {profile.bizCategory || "—"}
                          {profile.bizSubtype ? <span className="text-slate-400"> · </span> : null}
                          {profile.bizSubtype}
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-slate-500">
                          Jenis bisnis sudah tetap untuk profil ini. Bisnis yang berbeda memerlukan
                          profilnya sendiri — akses kota dan skor terikat pada profil ini. Hubungi
                          admin Anda untuk membuka kuncinya.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div>
              <FieldLabel hint="Pilihan ini menjadi pengganda berbobot dalam skor lokasi Anda.">Prioritas Lokasi</FieldLabel>
              <ChipGroup options={PRIORITIES} value={profile.priorities || []} onChange={(v) => set("priorities", v)} multi />
            </div>
          </div>
        </div>

        {/* ── Location & Market ── */}
        <div className="bg-white rounded-2xl border border-[#e8eaed] p-6 mb-5">
          <SectionHeader icon={MapPin} title="Lokasi & Pasar" subtitle="Sesuaikan geografi target dan profil pelanggan Anda." />

          <div className="flex flex-col gap-6">
            <div>
              <FieldLabel>Kota / Kabupaten Target</FieldLabel>
              <CityAutocomplete value={profile.city} onChange={(v) => set("city", v)} />
            </div>

            <div>
              <FieldLabel hint="100 m memberi presisi setingkat jalan dan memuat jauh lebih banyak sel. 1 km memberi gambaran umum sekota yang cepat.">
                Resolusi Grid
              </FieldLabel>
              <div className="flex gap-2">
                {[
                  { v: "100m", t: "100 m", d: "Presisi setingkat jalan" },
                  { v: "1km",  t: "1 km",  d: "Gambaran sekota · cepat" },
                ].map((o) => {
                  const sel = (profile.gridResolution || "100m") === o.v;
                  return (
                    <button
                      key={o.v}
                      onClick={() => set("gridResolution", o.v)}
                      style={sel ? { borderColor: ACCENT, background: "#f1f5f3" } : {}}
                      className={`flex-1 rounded-2xl border px-4 py-3 text-left transition-all ${
                        sel ? "" : "bg-white border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="text-sm font-bold" style={{ color: sel ? ACCENT : "#1b1b1b" }}>{o.t}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{o.d}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <FieldLabel hint="Seberapa jauh skor lokasi menghitung penduduk sebagai pasar Anda. Sudah dihitung di muka (tanpa biaya tambahan); juga menjadi dasar angka 'Populasi dalam X km' di Analisis Mendalam. 20 km setara skala metro.">
                Jangkauan Populasi
              </FieldLabel>
              <div className="flex flex-wrap gap-2">
                {[
                  { v: "1km",  t: "1 km"  },
                  { v: "3km",  t: "3 km"  },
                  { v: "5km",  t: "5 km"  },
                  { v: "7km",  t: "7 km"  },
                  { v: "10km", t: "10 km" },
                  { v: "15km", t: "15 km" },
                  { v: "20km", t: "20 km" },
                  { v: "25km", t: "25 km" },
                  { v: "30km", t: "30 km" },
                  { v: "35km", t: "35 km" },
                  { v: "40km", t: "40 km" },
                ].map((o) => {
                  const sel = (profile.populationCatchment || "5km") === o.v;
                  return (
                    <button
                      key={o.v}
                      onClick={() => set("populationCatchment", o.v)}
                      style={sel ? { borderColor: ACCENT, background: "#f1f5f3" } : {}}
                      className={`rounded-2xl border px-4 py-2.5 text-sm font-bold transition-all ${
                        sel ? "" : "bg-white border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <span style={{ color: sel ? ACCENT : "#1b1b1b" }}>{o.t}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <FieldLabel>Anggaran Sewa Bulanan</FieldLabel>
              <ChipGroup options={BUDGETS} value={profile.rentalBudget || ""} onChange={(v) => set("rentalBudget", v)} />
            </div>

            <div>
              <FieldLabel>Lingkungan Pilihan</FieldLabel>
              <ChipGroup options={ENVIRONMENTS} value={profile.environments || []} onChange={(v) => set("environments", v)} multi />
            </div>

            <div>
              <FieldLabel hint="Lokasi dengan kondisi ini akan mendapat skor lebih rendah.">
                <span className="flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" /> Kondisi yang Dihindari
                </span>
              </FieldLabel>
              <ChipGroup options={AVOID_CONDITIONS} value={profile.avoidConditions || []} onChange={(v) => set("avoidConditions", v)} multi danger />
            </div>
          </div>
        </div>

        {/* ── Target Market ── */}
        <div className="bg-white rounded-2xl border border-[#e8eaed] p-6 mb-5">
          <SectionHeader icon={Users} title="Target Pasar" subtitle="Profil pelanggan Anda yang dipakai dalam penilaian demografis." />

          <div className="flex flex-col gap-6">
            <div>
              <FieldLabel>Pelanggan Utama</FieldLabel>
              <ChipGroup options={CUSTOMERS} value={profile.primaryCustomers || []} onChange={(v) => set("primaryCustomers", v)} multi />
            </div>

            <div>
              <FieldLabel hint="Rincian usia & gender dari target pasar Anda.">Profil Demografis</FieldLabel>
              <DemographicSelector value={profile.ageGroups || initAgeGender()} onChange={(v) => set("ageGroups", v)} />
            </div>

            <div>
              <FieldLabel hint="Dicocokkan dengan daya beli tiap area. Personalisasi tunggal terkuat dalam peta Anda.">Pendapatan Pelanggan Target</FieldLabel>
              <ChipGroup options={MARKET_TIERS} value={profile.marketTier || ""} onChange={(v) => set("marketTier", v)} />
            </div>

            <div>
              <FieldLabel>Waktu Tersibuk</FieldLabel>
              <ChipGroup options={BUSY_TIMES} value={profile.busyTimes || []} onChange={(v) => set("busyTimes", v)} multi />
            </div>
          </div>
        </div>

        {/* ── Trade Areas ── */}
        {/* Revenue projection inputs. Basket size and frequency are the same
            multiplier for every cell, so they convert the map into rupiah but
            never reorder it — only Target Customer Income above moves ranking. */}
        <div className="bg-white rounded-3xl p-6 md:p-8 mb-5" style={{ boxShadow: "0 1px 3px rgba(60,64,67,0.08)" }}>
          <SectionHeader icon={Briefcase} title="Wilayah Perdagangan"
            subtitle="Nilai satu pelanggan — mengubah peta menjadi proyeksi penjualan" />
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel hint="Barang termurah yang Anda jual.">Harga Produk Terendah (Rp)</FieldLabel>
                <RupiahField value={profile.productPriceMin} onChange={(v) => set("productPriceMin", v)} placeholder="500.000" />
              </div>
              <div>
                <FieldLabel hint="Barang termahal yang Anda jual.">Harga Produk Tertinggi (Rp)</FieldLabel>
                <RupiahField value={profile.productPriceMax} onChange={(v) => set("productPriceMax", v)} placeholder="50.000.000" />
              </div>
            </div>

            <div>
              <FieldLabel hint="Rata-rata rupiah yang dibelanjakan satu pelanggan dalam sekali kunjungan.">Rata-rata Nilai Belanja (Rp)</FieldLabel>
              <RupiahField value={profile.basketSize} onChange={(v) => set("basketSize", v)} placeholder="10.000.000" />
            </div>

            <div>
              <FieldLabel hint="Seberapa sering pelanggan yang sama datang kembali.">Frekuensi Pembelian</FieldLabel>
              <div className="flex items-center gap-3">
                <input
                  inputMode="numeric"
                  value={profile.buyingFrequency ?? ""}
                  onChange={(e) => {
                    const d = e.target.value.replace(/\D/g, "");
                    set("buyingFrequency", d === "" ? "" : Number(d));
                  }}
                  placeholder="1"
                  style={{ outline: "none" }}
                  onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
                  onBlurCapture={(e) => (e.target.style.borderColor = "#dadce0")}
                  className="w-20 px-4 py-2.5 border border-[#dadce0] rounded-xl text-sm text-[#202124] placeholder-[#9aa0a6] bg-white transition-colors"
                />
                <ChipGroup options={FREQ_UNITS} value={profile.buyingFrequencyUnit || "per month"} onChange={(v) => set("buyingFrequencyUnit", v)} />
              </div>
            </div>

            <div>
              <FieldLabel hint="Opsional. Asumsi Anda sendiri — kami bandingkan dengan apa yang didukung data area tersebut.">Perkiraan Pangsa Pasar (%)</FieldLabel>
              <input
                inputMode="decimal"
                value={profile.marketExpectation ?? ""}
                onChange={(e) => set("marketExpectation", e.target.value.replace(/[^\d.]/g, ""))}
                placeholder="10"
                style={{ outline: "none" }}
                onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
                onBlurCapture={(e) => (e.target.style.borderColor = "#dadce0")}
                className="w-28 px-4 py-2.5 border border-[#dadce0] rounded-xl text-sm text-[#202124] placeholder-[#9aa0a6] bg-white transition-colors"
              />
            </div>
          </div>
        </div>

        {/* ── Market Anchors ── */}
        <div className="bg-white rounded-2xl border border-[#e8eaed] p-6 mb-5">
          <SectionHeader icon={Building} title="Acuan Pasar" subtitle="Bisnis yang keberadaannya di dekat lokasi menandakan target pasar Anda sudah ada." />
          <div className="flex flex-col gap-3">
            {(profile.benchmarks || []).map((b, i) => (
              <div key={i} className="flex flex-col gap-2 rounded-xl border border-[#e8eaed] p-3">
                <div className="flex items-center gap-2">
                  <input
                    value={b.name}
                    onChange={(e) => {
                      const next = (profile.benchmarks || []).map((x, idx) => idx === i ? { ...x, name: e.target.value } : x);
                      set("benchmarks", next);
                    }}
                    placeholder={i === 0 ? "misalnya, Starbucks, Alfamart..." : `Acuan ${i + 1}`}
                    style={{ outline: "none" }}
                    onFocusCapture={(e) => (e.target.style.borderColor = ACCENT)}
                    onBlurCapture={(e) => (e.target.style.borderColor = "#dadce0")}
                    className="flex-1 px-3 py-2 border border-[#dadce0] rounded-lg text-sm text-[#202124] placeholder-[#9aa0a6] bg-white transition-colors"
                  />
                  <button
                    onClick={() => set("benchmarks", (profile.benchmarks || []).filter((_, idx) => idx !== i))}
                    className="p-1.5 rounded-lg text-[#9aa0a6] hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {b.name.trim() && (
                  <div className="flex flex-col gap-1.5">
                    <div className="flex flex-wrap gap-1.5">
                      {REFERENCE_TYPES.map((t) => {
                        const sel = b.type === t.id;
                        return (
                          <button
                            key={t.id}
                            onClick={() => {
                              const next = (profile.benchmarks || []).map((x, idx) => idx === i ? { ...x, type: t.id } : x);
                              set("benchmarks", next);
                            }}
                            style={sel ? { background: ACCENT, borderColor: ACCENT, color: "#fff" } : {}}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${sel ? "" : "border-[#dadce0] bg-white text-[#3c4043] hover:bg-[#f8f9fa]"}`}
                          >
                            {idLabel(t.id)}
                          </button>
                        );
                      })}
                    </div>
                    {b.type && (
                      <p className="text-[11px] text-[#5f6368] leading-relaxed">
                        {REFERENCE_TYPES.find((t) => t.id === b.type)?.desc}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
            {(profile.benchmarks || []).length < limits.max_anchors && ((profile.benchmarks || []).slice(-1)[0]?.name || "").trim() && (
              <button
                onClick={() => set("benchmarks", [...(profile.benchmarks || []), { name: "", type: "Direct Competitor" }])}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-dashed border-[#dadce0] text-xs font-medium text-[#5f6368] hover:border-[#14543e] hover:text-[#14543e] transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Tambah Acuan
              </button>
            )}
            <p className="text-[11px] text-[#9aa0a6]">
              {(profile.benchmarks || []).filter((b) => b.name.trim()).length} dari {limits.max_anchors} ditambahkan
            </p>
          </div>
        </div>

        {/* ── POI Priority ── */}
        <div className="bg-white rounded-2xl border border-[#e8eaed] p-6 mb-5">
          <SectionHeader icon={MapPin} title="Prioritas POI" subtitle={`Pilih hingga ${limits.max_poi_priorities} kategori titik minat yang paling penting untuk lokasi Anda.`} />
          <div className="flex flex-wrap gap-2">
            {POI_OPTIONS.map((poi) => {
              const sel = (profile.poiPriorities || []).includes(poi);
              const maxed = (profile.poiPriorities || []).length >= limits.max_poi_priorities && !sel;
              return (
                <button
                  key={poi}
                  disabled={maxed}
                  onClick={() => {
                    const cur = profile.poiPriorities || [];
                    set("poiPriorities", sel ? cur.filter((p) => p !== poi) : [...cur, poi]);
                  }}
                  style={sel ? { background: ACCENT, borderColor: ACCENT, color: "#fff" } : {}}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    sel ? "" : maxed ? "border-[#dadce0] bg-white text-[#c0c0c0] cursor-not-allowed" : "border-[#dadce0] bg-white text-[#3c4043] hover:bg-[#f8f9fa]"
                  }`}
                >
                  {sel && <span className="mr-1 opacity-70">✓</span>}
                  {idLabel(poi)}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-[#9aa0a6]">
            {(profile.poiPriorities || []).length} / {limits.max_poi_priorities} dipilih
          </p>
        </div>

        {/* ── Save ── */}
        <button
          onClick={handleSave}
          style={{ backgroundColor: saved ? "#34a853" : ACCENT }}
          className="w-full py-3.5 rounded-full text-sm font-bold text-white flex items-center justify-center gap-2 hover:opacity-90 transition-all"
        >
          {saved ? (
            <><Check className="w-4 h-4" /> Tersimpan</>
          ) : (
            <><Save className="w-4 h-4" /> Simpan Perubahan</>
          )}
        </button>

        <p className="text-center text-[11px] text-[#9aa0a6] mt-3">
          Pengaturan yang disimpan berlaku untuk semua analisis mendatang di perangkat ini.
        </p>

        {/* ── Danger Zone ── */}
        <div className="mt-8 border border-red-100 rounded-2xl p-5">
          <p className="text-xs font-bold text-red-400 uppercase tracking-widest mb-1">Zona Berbahaya</p>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-[#202124]">Hapus Zona Tersimpan</p>
              <p className="text-[11px] text-[#9aa0a6] mt-0.5">
                {zoneCount > 0 ? `${zoneCount} zona tersimpan` : "Tidak ada zona tersimpan"} · digunakan di Analisis Mendalam &amp; Asisten AI
              </p>
            </div>
            <button
              onClick={handleClearZones}
              disabled={zoneCount === 0}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border border-red-200 text-red-500 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
            >
              <Trash2 className="w-3.5 h-3.5" /> Hapus Semua
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
